import React, { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useInView } from "framer-motion";
import { ArrowRight, Users, Clock, Award, Globe } from "lucide-react";
import { aboutData, manufacturingImages, nyImage } from "../data/mockData";

const FadeIn = ({ children, className = "", delay = 0 }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  return (
    <motion.div ref={ref} initial={{ opacity: 0, y: 35 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.7, delay, ease: "easeOut" }} className={className}>
      {children}
    </motion.div>
  );
};

const AboutPage = () => {
  const stats = [
    { label: "Years of Experience", value: aboutData.yearsExperience, Icon: Clock },
    { label: "Team Members", value: aboutData.teamSize, Icon: Users },
    { label: "Pieces Created", value: aboutData.piecesCreated, Icon: Award },
    { label: "Global Partners", value: aboutData.globalPartners, Icon: Globe }
  ];

  return (
    <div>
      {/* Hero */}
      <section className="relative h-[60vh] min-h-[400px] flex items-center justify-center bg-[#1a1a1a]">
        <div className="absolute inset-0 bg-cover bg-center opacity-40"
          style={{ backgroundImage: `url(${nyImage})` }} />
        <div className="absolute inset-0 bg-black/50" />
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1 }} className="relative z-10 text-center px-6">
          <h1 className="text-4xl md:text-5xl lg:text-6xl text-white font-light"
            style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            About Us
          </h1>
          <div className="h-[1px] w-16 bg-[#c8a96e] mx-auto mt-6" />
        </motion.div>
      </section>

      {/* Story */}
      <section className="py-24 bg-[#faf9f7]">
        <div className="max-w-[1100px] mx-auto px-6 lg:px-10">
          <FadeIn>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
              <div>
                <h3 className="text-[12px] tracking-[0.3em] uppercase text-[#c8a96e] mb-4"
                  style={{ fontFamily: "'Inter', sans-serif" }}>Our Story</h3>
                <h2 className="text-3xl md:text-4xl text-[#1a1a1a] font-light leading-tight"
                  style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                  A Legacy of Excellence
                </h2>
                <p className="mt-6 text-[#666] text-sm leading-[1.8]"
                  style={{ fontFamily: "'Inter', sans-serif" }}>
                  {aboutData.history}
                </p>
                <p className="mt-4 text-[#666] text-sm leading-[1.8]"
                  style={{ fontFamily: "'Inter', sans-serif" }}>
                  {aboutData.mission}
                </p>
              </div>
              <div className="relative">
                <img src={manufacturingImages.jewelry[0]} alt="Our Workshop"
                  className="w-full h-[400px] object-cover" loading="lazy" />
                <div className="absolute -bottom-4 -left-4 w-20 h-20 border-2 border-[#c8a96e]" />
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* Stats */}
      <section className="py-20 bg-[#1a1a1a]">
        <div className="max-w-[1100px] mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, i) => (
              <FadeIn key={stat.label} delay={i * 0.1}>
                <div className="text-center">
                  <stat.Icon className="w-6 h-6 text-[#c8a96e] mx-auto mb-4" strokeWidth={1.5} />
                  <div className="text-3xl md:text-4xl text-white font-light"
                    style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                    {stat.value}
                  </div>
                  <p className="mt-2 text-white/40 text-[11px] tracking-[0.15em] uppercase"
                    style={{ fontFamily: "'Inter', sans-serif" }}>
                    {stat.label}
                  </p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* Team & Craft */}
      <section className="py-24 bg-white">
        <div className="max-w-[1100px] mx-auto px-6 lg:px-10">
          <FadeIn>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
              <div className="relative">
                <img src={manufacturingImages.jewelry[1]} alt="Artisan at Work"
                  className="w-full h-[400px] object-cover" loading="lazy" />
              </div>
              <div>
                <h3 className="text-[12px] tracking-[0.3em] uppercase text-[#c8a96e] mb-4"
                  style={{ fontFamily: "'Inter', sans-serif" }}>Our Artisans</h3>
                <h2 className="text-3xl md:text-4xl text-[#1a1a1a] font-light"
                  style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                  Master Craftsmen
                </h2>
                <p className="mt-6 text-[#666] text-sm leading-[1.8]"
                  style={{ fontFamily: "'Inter', sans-serif" }}>
                  Our team of {aboutData.teamSize} skilled artisans and professionals bring together
                  decades of combined experience. From diamond cutting to jewelry setting, each
                  specialist contributes their unique expertise to create pieces of extraordinary beauty.
                </p>
                <p className="mt-4 text-[#666] text-sm leading-[1.8]"
                  style={{ fontFamily: "'Inter', sans-serif" }}>
                  We invest in continuous training and the latest technology to ensure our team
                  stays at the forefront of the industry. Our vertically integrated supply chain
                  allows us to maintain quality control at every stage.
                </p>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* Our Office */}
      <section className="py-24 bg-[#faf9f7]">
        <div className="max-w-[1100px] mx-auto px-6 lg:px-10">
          <FadeIn>
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl text-[#1a1a1a] font-light"
                style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                Visit Our Studio
              </h2>
            </div>
          </FadeIn>
          <FadeIn delay={0.1}>
            <div className="max-w-xl mx-auto p-10 bg-white text-center hover:shadow-lg transition-shadow duration-500">
              <h3 className="text-2xl text-[#1a1a1a]"
                style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                {aboutData.office.city}
              </h3>
              <p className="mt-4 text-[#888] text-sm leading-relaxed"
                style={{ fontFamily: "'Inter', sans-serif" }}>
                {aboutData.office.address}
              </p>
              <p className="mt-2 text-[#888] text-sm"
                style={{ fontFamily: "'Inter', sans-serif" }}>
                {aboutData.office.description}
              </p>
              <div className="mt-6 flex justify-center gap-6">
                <a href={`tel:${aboutData.office.phone}`}
                  className="text-[#c8a96e] text-sm hover:underline"
                  style={{ fontFamily: "'Inter', sans-serif" }}>
                  {aboutData.office.phone}
                </a>
                <a href={`mailto:${aboutData.office.email}`}
                  className="text-[#c8a96e] text-sm hover:underline"
                  style={{ fontFamily: "'Inter', sans-serif" }}>
                  {aboutData.office.email}
                </a>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-[#1a1a1a] text-center">
        <FadeIn>
          <h2 className="text-3xl md:text-4xl text-white font-light"
            style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            Let's Create Something Beautiful
          </h2>
          <Link to="/contact-us"
            className="inline-flex items-center gap-2 mt-8 px-8 py-3 border border-[#c8a96e] text-[#c8a96e] text-[12px] tracking-[0.15em] uppercase hover:bg-[#c8a96e] hover:text-[#1a1a1a] transition-all duration-500"
            style={{ fontFamily: "'Inter', sans-serif" }}>
            Contact Us <ArrowRight className="w-4 h-4" />
          </Link>
        </FadeIn>
      </section>
    </div>
  );
};

export default AboutPage;
